import { ChangeDetectionStrategy, Component, ChangeDetectorRef } from '@angular/core';
import { UrlBuilder } from '../../core';
import { GridDetails, ActionType, GridHeader } from '../../sample-tracking/tracking-overview/interfaces/grid';
import { ShipmentData } from '../interfaces/shipment-data';
import { ActivityLogger } from '../../activities/activity-logger';
import { RangeKind, DateRangeFilterValue } from '../../shared/time-frame-filter/timeframe-filter.component';
import * as moment from 'moment';
import { SiteShippingToolStorage } from '../service/site-shipping-tool-storage';
import { switchMap, map } from 'rxjs/operators';
import { ShipmentFormService } from '../shipment-form/service/shipment-form-service';
import { ShipmentFormStorage } from '../shipment-form/shipment-form-storage';
import { ConfirmDialogRequest } from '../shared/confirm-dialog/confirm-dialog.component';
import { CourierModelDialogService } from '../../shared/courier-model/courier-model-dialog-service';


@Component({
    selector: 'submitted-shipment-list',
    template: require('./submitted-shipment-list.component.html'),
    styles: [require('./submitted-shipment-list.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class SubmittedShipmentListComponent {

    public isLoadingCompleted = false;
    public gridDetails: GridDetails;
    public data: ShipmentData[];
    public header: GridHeader[] = [];
    public selectedRows: ShipmentData[] = [];
    public timeFrameRanges: RangeKind[] = [
        { key: 'today', value: 'Today', selected: false },
        { key: 'weekly', value: 'Last 7 days', selected: false },
        { key: 'monthly', value: 'Last 30 days', selected: true },
        { key: 'twoMonths', value: 'Last 60 days', selected: false },
        { key: 'quarterly', value: 'Last 90 days', selected: false },
    ];
    public selectedDateRange: DateRangeFilterValue;
    public pagePerItems = [25, 50, 75, 100, 250, 500];
    public defaultPageSize = 25;
    private isEmulated: boolean;

    constructor(
        private shippingStorage: SiteShippingToolStorage,
        private urlBuilder: UrlBuilder,
        private changeDetector: ChangeDetectorRef,
        private activityLogger: ActivityLogger,
        private createShipmentFormService: ShipmentFormService,
        private courierService: CourierModelDialogService,
        private shipmentFormStorage: ShipmentFormStorage,
    ) {
        this.PrepareHeaderColumn();
        this.selectedDateRange = this.getDefaultRange();
        this.loadShipmentDetails(this.selectedDateRange.dateBegin, this.selectedDateRange.dateEnd);
        this.isEmulated = this.createShipmentFormService.isUserEmulated();
        activityLogger.logActivity('Shipments - List View');
    }

    public onGridItemClick(event: any) {
        this.activityLogger.logActivity('Shipments - Submitted Shipments - Action : View Shipment');
        if (event && event.header.key === 'shipmentStatus') {
            this.courierService.showCourierDetailsDialog(event.data.lgxCourierShipmentId, event.data.shipmentStatus, event.data.numberOfContainers ? event.data.numberOfContainers : 0);
            return;
        }
        this.shipmentSummary(event.data.shipmentId);
    }

    public viewAllShipments() {
    }

    public loadShipmentDetails(fromDate?: string, toDate?: string) {
        this.shippingStorage.getShipmentList(fromDate, toDate).subscribe(gridData => {
            this.data = this.formatShipmentList(gridData);
            this.gridDetails = { header: this.header, data: this.data };
            this.isLoadingCompleted = true;
            this.changeDetector.markForCheck();
        });
    }

    public timeFrameFilterChange($event: DateRangeFilterValue) {
        if ($event === this.selectedDateRange && $event.rangeType !== 'dateRange') { return; }
        this.isLoadingCompleted = false;
        this.loadShipmentDetails($event.dateBegin, $event.dateEnd);
        this.selectedDateRange = $event;
    }

    public createShipment() {
        this.urlBuilder.esa().sampleManagement().siteShipping().createShipment().navigate();
    }

    public shipmentSummary(id: number) {
        this.urlBuilder.esa().sampleManagement().siteShipping().shipmentSummary(id).navigate();
    }

    public onRowsSelected($event: ShipmentData[]) {
        this.selectedRows = $event.filter(t => t.rowSelected);
    }

    public onPagePerItemsChange($event: any) {
        this.defaultPageSize = $event.pageSize;
    }

    private PrepareHeaderColumn() {
        this.header = [
            {
                displayText: 'Waybill Number',
                key: 'airwayBillNumber',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                displayText: 'Destination Name',
                key: 'destinationOrgName',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                displayText: 'Courier Name',
                key: 'courierName',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                displayText: 'Number of Samples Included in Shipment',
                key: 'numberOfContainers',
                defaultValue: '0',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                displayText: 'Protocols',
                key: 'protocolCode',
                sort: null,
                enableFilter: true,
                enableSearch: true,
            },
            {
                displayText: 'Created Date',
                key: 'dateCreated',
                sort: null,
                isDate: true,
                enableFilter: true,
                enableSearch: true,
            },
            {
                displayText: 'Date Submitted',
                key: 'dateSubmitted',
                sort: null,
                isDate: true,
                enableFilter: true,
                enableSearch: true,
            },
            {
                displayText: 'Shipment Status',
                key: 'shipmentStatus',
                sort: null,
                enableFilter: true,
                enableSearch: true,
                isLink: true,
                defaultValue: 'N/A'
            },
            {
                displayText: 'Action',
                key: 'null',
                sort: null,
                hideSort: true,
                actions: [
                    {
                        description: 'View Shipment',
                        actionType: ActionType.view,
                        cssClass: 'action view',
                        displayText: 'View',
                        helpTextClass: 'action helptext'
                    },
                ]
            },
        ];
    }

    private getDefaultRange(): DateRangeFilterValue {
        return {
            dateBegin: moment().subtract(30, 'd').format('YYYY-MM-DD'),
            dateEnd: moment().format('YYYY-MM-DD'),
            rangeType: 'monthly'
        };
    }

    private formatShipmentList(gridData: ShipmentData[]): ShipmentData[] {
        if (!gridData || gridData.length === 0) { return []; }
        let data: ShipmentData[] = [];
        gridData.forEach(shipmentData => {
            let index = data.findIndex(t => t.shipmentId === shipmentData.shipmentId);
            if (index === -1) {
                let filteredData = gridData.filter(t => t.shipmentId === shipmentData.shipmentId);
                shipmentData.numberOfContainers = filteredData && filteredData[0].investigatorSiteShipmentContainerId > 0 ? filteredData.length : 0;
                shipmentData.protocolCode = shipmentData.hasPermission ? shipmentData.protocolCode : null;
                shipmentData.dateCreated = shipmentData.dateCreated ? moment(shipmentData.dateCreated).format('DD-MMM-YYYY') : '';
                shipmentData.dateSubmitted = shipmentData.dateSubmitted ? moment(shipmentData.dateSubmitted).format('DD-MMM-YYYY') : '';
                data.push(shipmentData);
            } else {
                if (data[index].protocolCode !== shipmentData.protocolCode && shipmentData.hasPermission) {
                    if (shipmentData.protocolCode && data[index].protocolCode) {
                        data[index].protocolCode = data[index].protocolCode + ', ' + shipmentData.protocolCode;
                    } else if (!data[index].protocolCode) {
                        data[index].protocolCode = shipmentData.protocolCode;
                    }
                }
            }
        });
        return data;
    }

    private showWarning(data: ConfirmDialogRequest, showDeleteDialog = false) {
        let dialogRequest: ConfirmDialogRequest = { ...data };
        return this.createShipmentFormService.showConfirmDialog(dialogRequest, showDeleteDialog)
            .pipe(switchMap(dialog => dialog.onHide()), map(dialogResult => dialogResult));
    }
}